// Libraries
import { FC } from "react";
import { Title as DialogTitle } from "@radix-ui/react-dialog";
import { PiWarningFill } from "react-icons/pi";

// Components
import { Modal, ModalBody, ModalFooter } from "../modal";
import { Button } from "../buttons/Button";

// Types
import { AlertDialogProps } from "./AlertDialog.types";
import { cn } from "utils/cn";

export const AlertDialog: FC<AlertDialogProps> = ({
  type = "warning",
  isOpen,
  onClose,
  title,
  actions = {
    success: {
      text: "Continue",
      onSuccess: () => {},
    },
    cancel: "Cancel",
  },
  children,
}) => {
  const handleSuccess = () => {
    if (actions.success) actions.success.onSuccess();
    onClose();
  };

  const handleCancel = () => {
    if (typeof actions.cancel === "object" && actions.cancel.onCancel) {
      actions.cancel.onCancel();
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} maxWidth="max-w-md" position="top">
      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          {type === "warning" && (
            <PiWarningFill className="text-warning text-xl" data-testid="warning-icon" />
          )}
          {type === "error" && (
            <PiWarningFill className="text-danger text-xl" data-testid="error-icon" />
          )}
          <DialogTitle className="font-normal text-foreground text-left font-inter text-xl">
            {title}
          </DialogTitle>
        </div>
        <ModalBody showBorders={false}>
          {typeof children === "string" ? <p>{children}</p> : children}
        </ModalBody>
        <ModalFooter className="flex-row justify-end gap-2">
          {actions.cancel && (
            <Button
              onClick={handleCancel}
              color="gray"
              className="rounded-lg px-3 py-2 h-auto text-muted-foreground hover:bg-gradient-to-r from-none to-none hover:text-foreground hover:scale-105"
            >
              {typeof actions.cancel === "string" ? actions.cancel : actions.cancel.text}
            </Button>
          )}
          {actions.success && (
            <Button
              onClick={handleSuccess}
              variant={type === "error" ? "destructive" : "default"}
              className={cn(
                "rounded-lg px-3 py-2 h-auto hover:scale-105",
                type !== "error" &&
                  "text-primary hover:bg-gradient-to-r from-none to-none hover:text-primary"
              )}
            >
              {actions.success.text}
            </Button>
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default AlertDialog;
